/**
 * CoWork Context panel — the right-hand rail in CoWork mode (hybrid CoWork).
 * A connected-folder header, then the live Working-folders tree as the primary
 * surface (it auto-reveals and highlights the file the agent is currently
 * touching), with a compact Activity feed docked below. Clicking a file opens a
 * preview; collapses to a rail. Desktop-only.
 *
 * @module components/cowork/cowork-context-panel
 */

import { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  FilePlus,
  FileSearch,
  FolderOpen,
  HardDrive,
  ListChecks,
  Loader2,
  PanelRightClose,
  PanelRightOpen,
  RotateCw,
  Terminal,
  Trash2,
} from "lucide-react";

import { FilePreviewDialog, type FileSource } from "@/components/file-preview";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useCowork } from "@/hooks/use-cowork";
import { useCoworkStore } from "@/store/cowork-store";
import { cn } from "@/lib/utils";
import { CoworkFolderTree, type CoworkFolderTreeHandle } from "./cowork-folder-tree";
import { coworkFileSource } from "./cowork-file-source";
import type { WorkspaceChange } from "@/lib/desktop";
import { PlanTimeline } from "@/components/chat/plan/plan-timeline";
import { usePlanAutoScroll } from "@/components/chat/plan/use-plan-autoscroll";
import { useLivePlan } from "@/store/plan-store";

/** Compact relative time — Date.now() is fine in the renderer. */
function ago(at: number): string {
  const s = Math.max(0, Math.round((Date.now() - at) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  return `${Math.round(m / 60)}h`;
}


export function CoworkContextPanel() {
  const { deviceId, folderName, connected, changes, reveal } = useCowork();
  const collapsed = useCoworkStore((s) => s.contextPanelCollapsed);
  const toggle = useCoworkStore((s) => s.toggleContextPanel);
  const pending = useCoworkStore((s) => s.approvals.length);
  const reconnecting = useCoworkStore((s) => s.reconnecting);
  // The backend is authoritative: the executing model creates + advances the plan
  // (forced via prepareStep), so we render its statuses directly — no inference.
  // `useLivePlan` scopes it to the run, same as the chat card, so the section
  // disappears when the turn ends instead of parking a finished checklist here.
  const plan = useLivePlan();
  const planTasks = plan?.tasks ?? [];
  // Per-section collapse (Plan / Working folders / Activity). Collapsing one lets
  // the other expanded sections grow to fill via flex — see the section wrappers.
  const [sectionOpen, setSectionOpen] = useState<{ plan: boolean; folders: boolean; activity: boolean }>({
    plan: true,
    folders: true,
    activity: true,
  });
  const toggleSection = (k: "plan" | "folders" | "activity") =>
    setSectionOpen((s) => ({ ...s, [k]: !s[k] }));
  // Reuse the app's unified file-preview dialog (all file types + fullscreen).
  const [source, setSource] = useState<FileSource | null>(null);
  const openFile = (relPath: string) => setSource(coworkFileSource(relPath));
  // Refresh button lives on the "Working folders" heading; drive the tree via a ref.
  const treeRef = useRef<CoworkFolderTreeHandle>(null);
  const [treeBusy, setTreeBusy] = useState(false);

  // Mutations drive the tree's live auto-reload (reads don't change the tree).
  const mutationCount = useMemo(
    () => changes.reduce((n, c) => (c.kind === "read" ? n : n + 1), 0),
    [changes],
  );
  // Distinct files the agent created/edited/removed — for the collapsed-rail badge.
  const editedCount = useMemo(() => {
    const s = new Set<string>();
    for (const c of changes) if (c.kind === "write" || c.kind === "delete" || c.kind === "mkdir") s.add(c.path);
    return s.size;
  }, [changes]);
  // The file the agent most recently touched — the tree auto-expands to it and
  // highlights it, so the user sees what's being worked on without hunting.
  const activePath = useMemo(() => {
    for (let i = changes.length - 1; i >= 0; i--) {
      const c = changes[i];
      if (c.kind === "write" || c.kind === "read") return c.path;
    }
    return null;
  }, [changes]);
  // Chronological (newest last) so the list reads like a live stream/terminal.
  const activity = useMemo(() => changes.slice(-40), [changes]);
  const activityRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = activityRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [changes.length]);

  // Auto-scroll the plan timeline so the moving (in-progress) step stays in view
  // as tasks advance. Shared with the chat card so both surfaces behave the same.
  const planScrollRef = useRef<HTMLDivElement>(null);
  usePlanAutoScroll(planScrollRef, planTasks, sectionOpen.plan);

  // ---- collapsed rail ----
  if (collapsed) {
    return (
      <aside className="z-[1] flex w-11 shrink-0 flex-col items-center gap-3 border-l border-border/60 bg-background py-3">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={toggle}
              className="grid size-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <PanelRightOpen className="size-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="left">Open Context panel</TooltipContent>
        </Tooltip>
        <div className="grid size-8 place-items-center rounded-md bg-secondary/15 text-secondary" title="Working folder">
          <HardDrive className="size-4" />
        </div>
        {editedCount > 0 && (
          <span
            className="grid size-6 place-items-center rounded-full bg-warning/15 text-[10px] font-bold text-warning"
            title={`${editedCount} file(s) changed`}
          >
            {editedCount}
          </span>
        )}
      </aside>
    );
  }

  // ---- the rail ----
  return (
    <aside className="z-[1] flex w-[300px] shrink-0 flex-col border-l border-border/60 bg-background">
      {/* Unified preview dialog (all file types + fullscreen), reused from the app. */}
      <FilePreviewDialog source={source} open={!!source} onOpenChange={(o) => !o && setSource(null)} />
      <header className="flex items-center gap-2 border-b border-border/60 px-3.5 py-2.5">
        <span className="text-sm font-semibold text-foreground">Context</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={toggle}
              className="ml-auto grid size-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <PanelRightClose className="size-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="left">Collapse panel</TooltipContent>
        </Tooltip>
      </header>

      {/* fixed: connected folder */}
      <div className="border-b border-border/60 px-3 py-2.5">
        <div className="flex items-center gap-2.5 rounded-lg border border-border bg-gradient-to-b from-secondary/[0.07] to-transparent p-2.5">
          <div className="grid size-8 shrink-0 place-items-center rounded-md bg-secondary/15 text-secondary">
            <HardDrive className="size-4" />
          </div>
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-foreground" title={folderName ?? undefined}>
              {folderName}
            </div>
            <div
              className={cn(
                "flex items-center gap-1 text-[11px] font-medium",
                reconnecting ? "text-secondary" : connected ? "text-success" : "text-warning",
              )}
            >
              {reconnecting ? (
                <Loader2 className="size-3 animate-spin" />
              ) : (
                <span className={cn("size-1.5 rounded-full", connected ? "bg-success" : "bg-warning")} />
              )}
              {reconnecting ? "Reconnecting…" : connected ? "Connected" : "Reconnecting…"}
            </div>
          </div>
        </div>
      </div>

      {/* Plan — the agent's live task list. Content-sized with a pinned header;
          the timeline scrolls internally when there are many tasks. */}
      {planTasks.length > 0 &&
        (() => {
          const completed = planTasks.filter((t) => t.status === "completed").length;
          const total = planTasks.length;
          const open = sectionOpen.plan;
          return (
            <div className="flex shrink-0 flex-col border-b border-border/60 px-3 py-3">
              <button
                type="button"
                onClick={() => toggleSection("plan")}
                className="flex shrink-0 items-center gap-2 text-left"
              >
                <ChevronDown
                  className={cn("size-4 shrink-0 text-muted-foreground transition-transform", !open && "-rotate-90")}
                />
                <ListChecks className="size-[18px] shrink-0 text-secondary" />
                <span className="text-[15px] font-semibold text-foreground">Plan</span>
                <span className="ml-auto text-[11px] font-medium tabular-nums text-muted-foreground">
                  {completed}/{total}
                </span>
              </button>
              {open && (
                <>
                  {/* slim progress bar */}
                  <div className="mb-3 mt-2 h-1 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-success transition-all duration-500 ease-out"
                      style={{ width: `${total ? (completed / total) * 100 : 0}%` }}
                    />
                  </div>
                  {/* timeline scrolls internally past a few tasks; header stays put */}
                  {/* px/py so the active node's ring isn't sliced by the scroll box — see plan-checklist-card */}
                  <div ref={planScrollRef} className="scrollbar-hide max-h-[28vh] overflow-y-auto px-1.5 py-1">
                    <PlanTimeline tasks={planTasks} />
                  </div>
                </>
              )}
            </div>
          );
        })()}

      {/* Working folders — fills the space between Plan and Activity. The header
          is pinned; only the tree scrolls beneath it. */}
      {(() => {
        const open = sectionOpen.folders;
        return (
          <div className={cn("relative flex flex-col px-3 pt-3", open ? "min-h-0 flex-1" : "shrink-0 pb-2.5")}>
            {reconnecting && open && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-background/70 backdrop-blur-[1px]">
                <Loader2 className="size-5 animate-spin text-secondary" />
                <span className="text-xs font-medium text-muted-foreground">Reconnecting to your folder…</span>
              </div>
            )}
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={() => toggleSection("folders")}
                className="flex min-w-0 flex-1 items-center gap-2 text-left"
              >
                <ChevronDown
                  className={cn("size-4 shrink-0 text-muted-foreground transition-transform", !open && "-rotate-90")}
                />
                <FolderOpen className="size-[18px] shrink-0 text-secondary" />
                <span className="text-[15px] font-semibold text-foreground">Working folders</span>
              </button>
              {open && (
                <button
                  type="button"
                  onClick={() => treeRef.current?.refresh()}
                  title="Refresh"
                  className="grid size-6 shrink-0 place-items-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  <RotateCw className={cn("size-3.5", treeBusy && "animate-spin")} />
                </button>
              )}
            </div>
            {open && (
              <div className="scrollbar-hide mt-2 flex min-h-0 flex-1 flex-col overflow-y-auto">
                <CoworkFolderTree
                  ref={treeRef}
                  key={deviceId ?? "none"}
                  rootName={folderName}
                  onOpenFile={openFile}
                  onReveal={reveal}
                  nonce={mutationCount}
                  activePath={activePath}
                  onBusyChange={setTreeBusy}
                />
              </div>
            )}
          </div>
        );
      })()}

      {/* Activity — docked at the bottom, always minimal (compact live feed).
          Fixed small footprint; its own tight scroll keeps it out of the way. */}
      {(() => {
        const open = sectionOpen.activity;
        return (
          <div className="flex shrink-0 flex-col border-t border-border/60 bg-muted/20 px-3 py-2">
            <button
              type="button"
              onClick={() => toggleSection("activity")}
              className="flex items-center gap-2 text-left"
            >
              <ChevronDown
                className={cn("size-3.5 shrink-0 text-muted-foreground transition-transform", !open && "-rotate-90")}
              />
              <span className="text-[13px] font-semibold text-foreground">Activity</span>
              {pending > 0 ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-secondary/15 px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-secondary">
                  <span className="size-1.5 animate-pulse rounded-full bg-secondary" /> live
                </span>
              ) : activity.length > 0 ? (
                <span className="ml-auto rounded-full bg-muted px-1.5 text-[10px] font-bold tabular-nums text-muted-foreground">
                  {activity.length}
                </span>
              ) : null}
            </button>
            {open &&
              (activity.length === 0 ? (
                <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground/60">
                  Reads, writes and commands stream here as the agent works.
                </p>
              ) : (
                <div
                  ref={activityRef}
                  className="scrollbar-hide mt-1.5 flex max-h-[7.5rem] flex-col gap-px overflow-y-auto"
                >
                  {activity.map((c, i) => (
                    <ActivityRow key={`${c.path}-${c.at}-${i}`} change={c} />
                  ))}
                </div>
              ))}
          </div>
        );
      })()}
    </aside>
  );
}

function activityIcon(kind: WorkspaceChange["kind"]) {
  if (kind === "read") return { Icon: FileSearch, cls: "bg-muted text-muted-foreground" };
  if (kind === "exec") return { Icon: Terminal, cls: "bg-secondary/15 text-secondary" };
  if (kind === "delete") return { Icon: Trash2, cls: "bg-destructive/10 text-destructive" };
  if (kind === "mkdir") return { Icon: FolderOpen, cls: "bg-muted text-muted-foreground" };
  return { Icon: FilePlus, cls: "bg-success/15 text-success" };
}

function ActivityRow({ change }: { change: WorkspaceChange }) {
  const { Icon, cls } = activityIcon(change.kind);
  const verb =
    change.kind === "read"
      ? "Read"
      : change.kind === "exec"
        ? "Ran"
        : change.kind === "delete"
          ? "Deleted"
          : change.kind === "mkdir"
            ? "Created dir"
            : "Wrote";
  const label = change.kind === "exec" ? change.path : change.path.split("/").pop() ?? change.path;
  return (
    <div className="group flex items-center gap-2 rounded-md px-1 py-1 transition-colors hover:bg-accent/40">
      <span className={cn("grid size-5 shrink-0 place-items-center rounded-md", cls)}>
        <Icon className="size-3" />
      </span>
      <span className="min-w-0 flex-1 truncate text-[12px] leading-tight">
        <span className="text-muted-foreground/80">{verb} </span>
        <span className="font-mono text-foreground">{label}</span>
      </span>
      <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground/60">{ago(change.at)}</span>
    </div>
  );
}
